import * as types from './types';
import KanbanAPI from '../../../api/kanban'; // Caminho corrigido
import CompanyAPI from '../../../api/companies';

const toIsoStringIfEpoch = value => {
  if (typeof value === 'number') {
    return new Date(value * 1000).toISOString();
  }

  return value;
};

const hasKanbanFilterValue = value =>
  value !== '' && value !== null && value !== undefined;

const normalizeConversationCardData = cardData => {
  const sender = cardData.meta?.sender || {};
  const assignee = cardData.meta?.assignee || {};
  const team = cardData.meta?.team || {};

  return {
    title: sender.name || 'Contato sem nome',
    conversation_id: cardData.conversation_id,
    contact_id: sender.id,
    contact_avatar: sender.thumbnail,
    contact_phone: sender.phone_number,
    contact_email: sender.email,
    assignee_id: assignee.id || null,
    assignee_name: assignee.name || null,
    team_id: team.id || null,
    team_name: team.name || null,
    custom_attributes: cardData.custom_attributes || {},
    labels: cardData.labels || [],
    last_activity_at: toIsoStringIfEpoch(
      cardData.last_activity_at || cardData.timestamp
    ),
    created_at: toIsoStringIfEpoch(cardData.created_at),
    unread_count: cardData.unread_count || 0,
    status: cardData.status,
    priority: cardData.priority,
    inbox_id: cardData.inbox_id,
  };
};

const normalizeContactCardData = cardData => {
  return {
    title: cardData.name || 'Contato sem nome',
    contact_id: cardData.id,
    contact_avatar: cardData.thumbnail,
    contact_phone: cardData.phone_number,
    contact_email: cardData.email,
    assignee_id: cardData.assignee_id || null,
    assignee_name: cardData.assignee?.name || null,
    team_id: cardData.team_id || null,
    team_name: cardData.team?.name || null,
    custom_attributes: cardData.custom_attributes || {},
    labels: (cardData.labels || []).map(
      label => label.title || label.name || label
    ),
    bio: cardData.additional_attributes?.description || '',
    last_activity_at: toIsoStringIfEpoch(cardData.last_activity_at),
    created_at: toIsoStringIfEpoch(cardData.created_at),
  };
};

export default {
  async fetchFunnels({ commit, state }) {
    commit(types.SET_LOADING, { flag: 'funnels', value: true });
    commit(types.SET_ERROR, { flag: 'funnels', error: null });
    try {
      const response = await KanbanAPI.getFunnels();
      commit(types.SET_FUNNELS, response.data);
      // Seleciona o primeiro funil por padrão, se houver algum
      if (response.data.length && !state.selectedFunnelId) {
        commit(types.SET_SELECTED_FUNNEL_ID, response.data[0].id);
      }
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'funnels',
        error: error.message || 'Falha ao buscar funis',
      });
    } finally {
      commit(types.SET_LOADING, { flag: 'funnels', value: false });
    }
  },

  // Reutilizar action de labels ou criar uma específica
  async fetchAvailableLabels({ commit, dispatch, rootGetters }) {
    commit(types.SET_LOADING, { flag: 'labels', value: true });
    commit(types.SET_ERROR, { flag: 'labels', error: null });

    try {
      // Primeiro tentamos buscar do módulo labels (que pode já ter carregado as labels)
      if (rootGetters['labels/getLabels'].length === 0) {
        await dispatch('labels/get', null, { root: true });
      }

      // Agora pegamos as labels do state do módulo labels
      const labels = rootGetters['labels/getLabels'];

      // E salvamos no state do kanban
      commit(types.SET_AVAILABLE_LABELS, labels);
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'labels',
        error: error.message || 'Falha ao buscar labels',
      });
    } finally {
      commit(types.SET_LOADING, { flag: 'labels', value: false });
    }
  },

  async fetchCards({ commit }, payload) {
    let funnelId;
    let labelId;
    let page;
    let search;
    let cardType;
    let cardTypes;
    let assigneeId;
    let teamId;
    let sortOrder;

    if (typeof payload === 'object' && payload !== null) {
      funnelId = payload.funnelId;
      labelId = payload.labelId;
      page = payload.page || 1;
      search = payload.search || '';
      cardType = payload.cardType;
      cardTypes = payload.cardTypes;
      assigneeId = payload.assigneeId;
      teamId = payload.teamId;
      sortOrder = payload.sortOrder;
    } else {
      funnelId = payload;
      page = 1;
      search = '';
    }

    if (!funnelId) return;

    // Se for uma busca específica (paginação/pesquisa), não limpamos tudo e setamos loading específico
    const isInitialLoad = !labelId && page === 1 && !search;

    if (isInitialLoad) {
      commit(types.SET_LOADING, { flag: 'cards', value: true });
      commit(types.SET_ERROR, { flag: 'cards', error: null });
      commit(types.SET_CARDS_BY_LABEL, {});
    } else {
      // Poderíamos ter um loading state por label, mas por enquanto vamos usar o global ou um novo
    }

    try {
      // Prepara params para a API
      const params = {};
      if (labelId) params.label_id = labelId;
      if (page) params.page = page;
      if (search) params.search = search;
      if (Array.isArray(cardTypes) && cardTypes.length) {
        params.card_types = cardTypes;
      } else if (cardType && cardType !== 'all') {
        params.card_type = cardType;
      }
      if (hasKanbanFilterValue(assigneeId)) params.assignee_id = assigneeId;
      if (hasKanbanFilterValue(teamId)) params.team_id = teamId;
      if (hasKanbanFilterValue(sortOrder)) params.sort_order = sortOrder;

      const response = await KanbanAPI.getCards(funnelId, params);

      if (isInitialLoad) {
        commit(types.SET_CARDS_BY_LABEL, response.data);
      } else {
        // Para paginação/pesquisa, fazemos append ou update específico
        commit(types.UPDATE_CARDS_FOR_LABEL, {
          labelId,
          data: response.data[labelId] || response.data, // Fallback se a API retornar direto o objeto
          isSearch: !!search,
          page,
        });
      }
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'cards',
        error: error.message || 'Falha ao buscar cards',
      });
    } finally {
      if (isInitialLoad) {
        commit(types.SET_LOADING, { flag: 'cards', value: false });
      }
    }
  },

  async fetchMoreCards({ dispatch }, payload) {
    return dispatch('fetchCards', payload);
  },

  async fetchColumnDetails(
    { commit },
    { funnelId, labelId, cardType, cardTypes, search, assigneeId, teamId }
  ) {
    try {
      const response = await KanbanAPI.getColumnDetails(
        funnelId,
        labelId,
        cardTypes || cardType,
        search,
        assigneeId,
        teamId
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching column details:', error);
      throw error;
    }
  },

  async createFunnel({ commit }, { kanban_funnel }) {
    commit(types.SET_LOADING, { flag: 'create', value: true });
    commit(types.SET_ERROR, { flag: 'create', error: null });
    try {
      const response = await KanbanAPI.createFunnel({ kanban_funnel });
      commit(types.ADD_FUNNEL, response.data);
      commit(types.SET_SELECTED_FUNNEL_ID, response.data.id); // Seleciona o novo funil
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'create',
        error: error.message || 'Falha ao criar funil',
      });
      throw error; // Re-lança o erro para que o componente possa tratá-lo
    } finally {
      commit(types.SET_LOADING, { flag: 'create', value: false });
    }
  },

  async updateFunnel({ commit }, { id, kanban_funnel }) {
    commit(types.SET_LOADING, { flag: 'update', value: true });
    commit(types.SET_ERROR, { flag: 'update', error: null });
    try {
      const response = await KanbanAPI.updateFunnel(id, { kanban_funnel });
      commit(types.UPDATE_FUNNEL_IN_LIST, response.data);
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'update',
        error: error.message || 'Falha ao atualizar funil',
      });
    } finally {
      commit(types.SET_LOADING, { flag: 'update', value: false });
    }
  },

  async deleteFunnel({ commit, dispatch, state }, funnelId) {
    commit(types.SET_LOADING, { flag: 'delete', value: true });
    commit(types.SET_ERROR, { flag: 'delete', error: null });
    try {
      await KanbanAPI.deleteFunnel(funnelId);
      commit(types.REMOVE_FUNNEL_FROM_LIST, funnelId);
      // Se o funil excluído era o selecionado, busca cards do novo funil selecionado (se houver)
      const newSelectedId = state.selectedFunnelId; // O ID pode ter sido atualizado na mutation
      if (newSelectedId) {
        dispatch('fetchCards', newSelectedId);
      } else {
        commit(types.SET_CARDS_BY_LABEL, {}); // Limpa cards se não houver mais funis
      }
    } catch (error) {
      commit(types.SET_ERROR, {
        flag: 'delete',
        error: error.message || 'Falha ao excluir funil',
      });
    } finally {
      commit(types.SET_LOADING, { flag: 'delete', value: false });
    }
  },

  // Action para mover o card com atualização instantânea e API em segundo plano
  async moveCard(
    { commit, dispatch, state, rootGetters },
    {
      cardId,
      conversationId,
      contactId,
      companyId,
      sourceLabelId,
      targetLabelId,
      sourceLabelTitle,
      targetLabelTitle,
      type,
    }
  ) {
    // 1. Atualização visual instantânea (totalmente otimista)
    commit(types.MOVE_CARD, { cardId, sourceLabelId, targetLabelId });

    // Atualizar imediatamente a data de última atividade do card
    const currentDate = new Date();
    const currentTimestamp = currentDate.toISOString();

    // Atualizamos com timestamps em formato ISO para garantir consistência
    commit(types.UPDATE_CARD_DATA, {
      labelId: targetLabelId,
      cardId: cardId,
      updatedData: {
        last_activity_at: currentTimestamp,
        created_at: currentTimestamp, // Atualizamos também created_at como fallback
      },
    });

    // Obter informações de labels se não fornecidas
    if (!sourceLabelTitle || !targetLabelTitle) {
      const sourceLabel = state.availableLabels.find(
        label => label.id === sourceLabelId
      );
      const targetLabel = state.availableLabels.find(
        label => label.id === targetLabelId
      );

      if (!sourceLabel || !targetLabel) {
        console.error('KANBAN.ERROR.LABELS_NOT_FOUND');
        return; // Falhar silenciosamente sem afetar a experiência
      }

      sourceLabelTitle = sourceLabel.title;
      targetLabelTitle = targetLabel.title;
    }

    // Verificar tipo de card
    const isContactCard = type === 'contact';
    const isCompanyCard = type === 'company';

    // 2. Colocar toda a lógica de API em segundo plano para não bloquear a UI
    try {
      let apiResponse = null;

      if (isContactCard) {
        // Para cartões de contato
        const cleanContactId =
          typeof contactId === 'string' && contactId.startsWith('contact_')
            ? contactId.replace('contact_', '')
            : contactId;

        if (!cleanContactId) {
          throw new Error('ID do contato não informado');
        }

        apiResponse = await KanbanAPI.updateContactLabelsByKanban(
          cleanContactId,
          sourceLabelTitle,
          targetLabelTitle
        );
      } else if (isCompanyCard) {
        const cleanCompanyId =
          typeof companyId === 'string' && companyId.startsWith('company_')
            ? companyId.replace('company_', '')
            : companyId;

        if (!cleanCompanyId) {
          throw new Error('ID da empresa não informado');
        }

        const cardsForTarget = state.cardsByLabel[targetLabelId]?.cards || [];
        const movedCard = cardsForTarget.find(card => card.id === cardId) || {};
        const labels = Array.isArray(movedCard.labels) ? movedCard.labels : [];
        const nextLabels = [
          ...new Set(
            labels
              .filter(label => label !== sourceLabelTitle)
              .concat(targetLabelTitle)
          ),
        ];
        apiResponse = await CompanyAPI.updateCompanyLabels(
          cleanCompanyId,
          nextLabels
        );
      } else {
        // Para cartões de conversa
        if (!conversationId) {
          throw new Error('ID da conversa não informado');
        }

        // Atualiza o servidor
        apiResponse = await KanbanAPI.updateConversationLabelsByKanban(
          conversationId,
          sourceLabelTitle,
          targetLabelTitle
        );
      }

      // 3. Atualizar o card com dados frescos do servidor (usando dados da resposta do update_labels_by_kanban)
      if (apiResponse && apiResponse.data) {
        const updatedData = apiResponse.data;
        const updatedLabels = Array.isArray(updatedData.labels)
          ? updatedData.labels
          : [targetLabelTitle];

        commit(types.UPDATE_CARD_DATA, {
          labelId: targetLabelId,
          cardId: cardId,
          updatedData: {
            tasks_count: updatedData.tasks_count || 0,
            has_overdue_tasks: updatedData.has_overdue_tasks || false,
            notes_count: updatedData.notes_count || 0,
            labels: updatedLabels,
            last_activity_at: updatedData.last_activity_at || currentTimestamp,
            // Para conversas, também atualizar unread_count e status
            ...(isContactCard || isCompanyCard
              ? {}
              : {
                  unread_count: updatedData.unread_count || 0,
                  status: updatedData.status,
                }),
          },
        });

        if (isContactCard) {
          const cleanContactId =
            typeof contactId === 'string' && contactId.startsWith('contact_')
              ? contactId.replace('contact_', '')
              : contactId;
          const normalizedContactId = Number(cleanContactId);
          const currentContact =
            rootGetters['contacts/getContact'](normalizedContactId);

          dispatch(
            'contactLabels/setContactLabel',
            {
              id: normalizedContactId,
              data: updatedLabels,
            },
            { root: true }
          );

          if (currentContact?.id) {
            dispatch(
              'contacts/setContact',
              {
                ...currentContact,
                labels: updatedLabels,
              },
              { root: true }
            );
          }
        } else {
          const normalizedConversationId = Number(conversationId);
          const currentConversation = rootGetters.getConversationById(
            normalizedConversationId
          );

          dispatch(
            'conversationLabels/setConversationLabel',
            {
              id: normalizedConversationId,
              data: updatedLabels,
            },
            { root: true }
          );

          if (currentConversation) {
            dispatch(
              'updateConversation',
              {
                ...currentConversation,
                labels: updatedLabels,
              },
              { root: true }
            );
          }
        }
      }
    } catch (error) {
      console.error(`Erro ao mover card ${cardId}:`, error);

      // 4. Reverter a movimentação otimista em caso de erro
      commit(types.MOVE_CARD, {
        cardId,
        sourceLabelId: targetLabelId, // Reverter: source vira target
        targetLabelId: sourceLabelId, // Reverter: target vira source
      });

      // Mostrar erro para o usuário (se necessário)
      // useAlert?.($t('KANBAN.MOVE_ERROR'));

      throw error; // Re-lançar o erro para que o componente possa lidar com ele
    }
  },

  // Action para atualizar dados de um card específico (chamada por outros stores)
  async refreshCardData({ commit }, { cardId, type, updatedData = null }) {
    try {
      let cardData = null;

      if (!updatedData) {
        // Se não foram fornecidos dados atualizados, buscar do servidor
        if (type === 'contact') {
          const contactIdClean = cardId.replace('contact_', '');
          const response = await KanbanAPI.getContactDetails(contactIdClean);
          cardData = normalizeContactCardData(
            response.data.payload || response.data
          );

          // Como a API padrão de contatos não retorna dados de tarefas,
          // precisamos buscar esses dados separadamente se necessário
          if (!cardData.tasks_count && cardData.tasks_count !== 0) {
            // Para cards de contato, vamos assumir valores default seguros
            // O ideal seria ter uma API específica para dados do kanban
            cardData.tasks_count = 0;
            cardData.has_overdue_tasks = false;
            cardData.notes_count = 0;
          }
        } else if (type === 'conversation') {
          const response = await KanbanAPI.getConversationDetails(cardId);
          cardData = normalizeConversationCardData(response.data);

          // Similarmente para conversas
          if (!cardData.tasks_count && cardData.tasks_count !== 0) {
            cardData.tasks_count = 0;
            cardData.has_overdue_tasks = false;
            cardData.notes_count = 0;
          }
        }
      } else {
        cardData = updatedData;
      }

      if (cardData) {
        // Usar a mutation reativa para atualizar o card
        commit(types.UPDATE_CARD_DATA_REACTIVE, {
          cardId: cardId,
          updatedData: {
            ...cardData,
            tasks_count: cardData.tasks_count || 0,
            has_overdue_tasks: cardData.has_overdue_tasks || false,
            notes_count: cardData.notes_count || 0,
            is_stagnant: cardData.is_stagnant || false,
            stagnant_for_days: cardData.stagnant_for_days || 0,
            labels: cardData.labels || [],
            unread_count: cardData.unread_count || 0,
          },
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar dados do card:', error);
    }
  },
};
